// The Custom layout: widgets placed on a snap-to grid, moved and resized by the viewer.
// Positions are grid cells (not pixels), so one arrangement fits any screen size.

export type GridWidgetId = 'clock' | 'location' | 'weather' | 'calendar' | 'lists' | 'news' | 'text' | 'planner' | 'powerbi';

export interface GridBox {
  id: GridWidgetId;
  /** Column and row of the top-left cell, from 0. */
  x: number;
  y: number;
  /** Width and height in cells. */
  w: number;
  h: number;
}

export type GridShape = 'landscape' | 'portrait';

/** Roughly square cells on 16:9 and 9:16 screens. */
export const GRID: Record<GridShape, { cols: number; rows: number }> = {
  landscape: { cols: 12, rows: 8 },
  portrait: { cols: 6, rows: 12 },
};

export const MIN_SIZE: Record<GridWidgetId, { w: number; h: number }> = {
  clock: { w: 2, h: 1 },
  location: { w: 2, h: 2 },
  weather: { w: 2, h: 2 },
  calendar: { w: 2, h: 2 },
  lists: { w: 2, h: 2 },
  news: { w: 2, h: 1 },
  text: { w: 2, h: 1 },
  planner: { w: 3, h: 2 },
  powerbi: { w: 3, h: 2 },
};

/** Size a widget gets when it is placed automatically (e.g. switched on after the layout was made). */
const NEW_SIZE: Record<GridWidgetId, { w: number; h: number }> = {
  clock: { w: 4, h: 2 },
  location: { w: 3, h: 3 },
  weather: { w: 3, h: 3 },
  calendar: { w: 3, h: 4 },
  lists: { w: 3, h: 3 },
  news: { w: 4, h: 2 },
  text: { w: 4, h: 2 },
  planner: { w: 6, h: 4 },
  powerbi: { w: 6, h: 4 },
};

export const DEFAULT_GRID: Record<GridShape, GridBox[]> = {
  landscape: [
    { id: 'calendar', x: 0, y: 0, w: 3, h: 5 },
    { id: 'lists', x: 3, y: 0, w: 3, h: 3 },
    { id: 'location', x: 9, y: 0, w: 3, h: 3 },
    { id: 'weather', x: 9, y: 3, w: 3, h: 3 },
    { id: 'clock', x: 0, y: 5, w: 4, h: 3 },
    { id: 'news', x: 5, y: 6, w: 4, h: 2 },
  ],
  portrait: [
    { id: 'clock', x: 0, y: 0, w: 4, h: 2 },
    { id: 'location', x: 0, y: 2, w: 3, h: 3 },
    { id: 'weather', x: 3, y: 2, w: 3, h: 3 },
    { id: 'calendar', x: 0, y: 5, w: 3, h: 4 },
    { id: 'lists', x: 3, y: 5, w: 3, h: 4 },
    { id: 'news', x: 0, y: 9, w: 6, h: 2 },
  ],
};

export const overlaps = (a: GridBox, b: GridBox): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const inside = (b: GridBox, shape: GridShape): boolean =>
  b.x >= 0 && b.y >= 0 && b.x + b.w <= GRID[shape].cols && b.y + b.h <= GRID[shape].rows;

/** A box can go here if it's on the grid, not below its minimum size, and clear of the others. */
export function fits(box: GridBox, others: GridBox[], shape: GridShape): boolean {
  const min = MIN_SIZE[box.id];
  return inside(box, shape) && box.w >= min.w && box.h >= min.h && others.every((o) => o.id === box.id || !overlaps(box, o));
}

/** Replaces one box if the change fits; otherwise returns the list unchanged. */
export function tryUpdate(boxes: GridBox[], next: GridBox, shape: GridShape): GridBox[] {
  return fits(next, boxes, shape) ? boxes.map((b) => (b.id === next.id ? next : b)) : boxes;
}

/** First free spot (top-left first) for a widget, shrinking towards its minimum size if needed. */
export function findSpace(id: GridWidgetId, boxes: GridBox[], shape: GridShape): GridBox | null {
  const { cols, rows } = GRID[shape];
  const start = NEW_SIZE[id];
  const min = MIN_SIZE[id];
  for (let h = Math.min(start.h, rows); h >= min.h; h--) {
    for (let w = Math.min(start.w, cols); w >= min.w; w--) {
      for (let y = 0; y + h <= rows; y++) {
        for (let x = 0; x + w <= cols; x++) {
          const box = { id, x, y, w, h };
          if (fits(box, boxes, shape)) return box;
        }
      }
    }
  }
  return null;
}

/**
 * The boxes to draw: saved positions for the widgets that are on (invalid ones dropped), plus
 * a free spot for any widget switched on that has no position yet. A widget that can't fit
 * anywhere is left out rather than drawn on top of another.
 */
export function arrange(saved: GridBox[] | undefined, visible: GridWidgetId[], shape: GridShape): GridBox[] {
  const out: GridBox[] = [];
  for (const b of saved ?? []) {
    if (visible.includes(b.id) && !out.some((o) => o.id === b.id) && fits(b, out, shape)) out.push(b);
  }
  for (const id of visible) {
    if (out.some((o) => o.id === id)) continue;
    const spot = findSpace(id, out, shape);
    if (spot) out.push(spot);
  }
  return out;
}

/** Keeps the positions of widgets that are switched off (so they return to the same place) when saving edits. */
export function mergeSaved(saved: GridBox[] | undefined, edited: GridBox[]): GridBox[] {
  return [...edited, ...(saved ?? []).filter((b) => !edited.some((e) => e.id === b.id) && edited.every((e) => !overlaps(e, b)))];
}

/** Next box to select with the remote: reading order, wrapping round. */
export function nextBox(boxes: GridBox[], current: GridWidgetId | null, step: 1 | -1): GridWidgetId | null {
  const order = [...boxes].sort((a, b) => a.y - b.y || a.x - b.x);
  if (!order.length) return null;
  const i = order.findIndex((b) => b.id === current);
  if (i < 0) return order[0]!.id;
  return order[(i + step + order.length) % order.length]!.id;
}
